import { montarParametros, type Parametros } from '@favo/core';

import type { ChaveMes, Integrador, PerfilMeses } from '@/dados';

import type { Cobertura } from './cobertura';
import { CHAVES, inteiroDe, type LinhaMes } from './meses';

/* Tudo que o histórico digitado diz, num lugar só.
 *
 * Existe separado da tela porque quem SALVA e quem MOSTRA olham para o mesmo
 * fechamento: o perfil e o "tem histórico" vão para o banco, o resto vira
 * leitura. Duas contas paralelas divergiriam no primeiro ajuste. */

export type ResumoHistorico = {
  /** Só os meses cobertos, na ordem. */
  chavesAtivas: readonly ChaveMes[];
  /** O que será gravado em `sazonalidade.meses`. */
  perfil: PerfilMeses;
  /** Falso quando tudo está zerado: dado vazio não pode virar dado falso. */
  temHistorico: boolean;
  totalProjetos: number;
  parametros: Parametros;
  faturamentoMedio: number | null;
  pesoDaFixa: number | null;
};

export function resumirHistorico({
  meses,
  cobertura,
  integrador,
}: {
  meses: Record<ChaveMes, LinhaMes>;
  /** `null` enquanto ninguém escolheu: não há histórico a resumir. */
  cobertura: Cobertura | null;
  integrador: Integrador | null;
}): ResumoHistorico {
  // Só os meses visíveis contam. Reduzir a cobertura esconde as linhas de trás
  // sem apagar o que foi digitado: quem corta é o salvamento.
  const chavesAtivas = cobertura === null ? [] : CHAVES.slice(0, cobertura);

  const perfil: PerfilMeses = {};
  let totalProjetos = 0;
  let totalValor = 0;
  for (const chave of chavesAtivas) {
    const projetos = inteiroDe(meses[chave].projetos);
    const valor = meses[chave].valor ?? 0;
    perfil[chave] = { projetos, valor };
    totalProjetos += projetos;
    totalValor += valor;
  }

  /* Volume anual e ticket médio saem do MOTOR, não de uma conta desta tela:
     é o mesmo montarParametros que o servidor roda, então a projeção mostrada
     aqui é a que vai valer no preço. `projetos_mes` entra nulo porque é isso
     que o salvamento grava: o histórico é a única fonte de volume da tela. */
  const parametros = montarParametros({
    integrador: {
      ...(integrador ?? {}),
      projetos_mes: null,
      sazonalidade: cobertura === null ? null : { meses: perfil, cobertura },
    },
  });

  // Média por MÊS INFORMADO (não por doze): é o fechamento do que se digitou.
  const faturamentoMedio = cobertura !== null && totalValor > 0 ? totalValor / cobertura : null;
  const pesoDaFixa =
    faturamentoMedio !== null && faturamentoMedio > 0
      ? parametros.despFixa / faturamentoMedio
      : null;

  return {
    chavesAtivas,
    perfil,
    temHistorico: totalProjetos > 0 || totalValor > 0,
    totalProjetos,
    parametros,
    faturamentoMedio,
    pesoDaFixa,
  };
}
